import { useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import { PermissionBit, QuickLookTasks } from "../../api";
import { ApiConnector } from "../../api/api-connector";
import { hasPermission } from "../../constants/permissions";
import {
  HeartCoreQuickLook,
  PatientQuickLook,
  QuickLook,
} from "../../models/quick-look-analyses";
import { Button } from "../ui-kit/button";
import styles from "./quick-look-button.module.css";

export interface QuickLookButtonProps {
  model: QuickLook;
  label: string;
  resultLabel: string;
  disabled?: boolean;
  size?: "large" | "small";
}

export const QuickLookButton = observer(function QuickLookButton({
  model,
  label,
  resultLabel,
  disabled,
  size = "large",
}: QuickLookButtonProps) {
  return (
    <Button
      variant={size === "large" ? "large" : "default"}
      pending={model.processing}
      disabled={disabled}
      onClick={() => {
        if (model.resultUrl) {
          window.open(model.resultUrl, "_blank");
        } else {
          void model.submit();
        }
      }}
      className={
        size === "large" ? styles.heartcoreButton : styles.heartcoreButtonSmall
      }
    >
      {model.resultUrl ? resultLabel : label}
    </Button>
  );
});

export const QuickLookPerLevel = observer(function QuickLookPerLevel({
  api,
  patientId,
}: {
  api: ApiConnector;
  patientId: number;
}) {
  const { t } = useLingui();
  const isDri = hasPermission(api.permissions, PermissionBit.ACCESS_DRI);
  const isLvfp =
    !isDri && hasPermission(api.permissions, PermissionBit.ACCESS_LVFP);
  const isHeartCore =
    !isLvfp &&
    hasPermission(api.permissions, PermissionBit.RUN_HEART_CORE_QUICKLOOK);

  const model = useMemo<QuickLook | null>(() => {
    if (isDri) {
      return new PatientQuickLook(
        api,
        QuickLookTasks.hf_dri_timeseries_ql,
        patientId,
      );
    }

    if (isLvfp || isHeartCore) {
      return new HeartCoreQuickLook(api, patientId, 12, 3);
    }

    return null;
  }, [api, isDri, isLvfp, isHeartCore, patientId]);

  if (!model) {
    return null;
  }

  const buttonLabel = isDri
    ? "DRI HeartCore"
    : isLvfp
      ? "LVFP HeartCore"
      : "HeartCore";

  return (
    <QuickLookButton
      label={buttonLabel}
      resultLabel={t`View HeartCore`}
      model={model}
      size="small"
    />
  );
});
